import { useEffect, useMemo, useState, type ReactNode } from 'react'
import Markdown from '@/app/common/Markdown'
import {
  markdownTimelineSegmentId,
  sectionTimelineSegmentId,
} from '@/app/playbook/timeline/timelineUtils'
import {
  type PlanId,
  type ResolvedBranchNode,
  type ResolvedBranchNodeSection,
} from '@/lib/branchTypes'
import { type ChunkAST } from '@/lib/parseMarkdown'
import { type PerspectiveCategory } from '@/app/common/blocks/perspectiveTabContext'
import { getPlaybookBranchNodeChunks } from './playbookBranchNodeMarkdownUtils'
import { PlaybookEpilogue } from './PlaybookEpilogue'
import { PerspectivePovBody } from './PerspectivePovBody'
import { EndingCollapse, useSequencedEnding, type EndingKey } from './EndingCollapse'
import { ScenarioEndFooter } from '../ScenarioEndFooter'

const FADE_OUT_MASK_IMAGE = 'linear-gradient(to bottom, black 0%, black 40%, transparent 100%)'

type RenderedSection = {
  key: string
  section?: ResolvedBranchNodeSection
  chunks: ChunkAST[]
}

export const PlaybookBranchNodeMarkdownContent = ({
  nodeId,
  node,
  isEpilogueExpanded = false,
  activePov = null,
  perspectiveBodies,
  onPlanHover,
  onBranchHover,
  fadeOut = false,
  beforeEpilogue,
  showScenarioEndCap = false,
  proseClassName,
}: {
  nodeId: string
  node: ResolvedBranchNode
  isEpilogueExpanded?: boolean
  // Which POV ending (if any) is expanded in place, and the bodies to render.
  // Mutually exclusive with the epilogue; mounts at the same end-cap site as it.
  activePov?: Exclude<PerspectiveCategory, 'mainScenario'> | null
  perspectiveBodies?: { public?: string; insider?: string }
  onPlanHover?: (planId: PlanId | null) => void
  onBranchHover?: (targetId: string | null) => void
  fadeOut?: boolean
  // Branch-specific landing rendered just above the epilogue (the end-of-branch
  // timeline + reviewer box). It needs full playbook nav, so it's built by the
  // caller and passed in rather than constructed here.
  beforeEpilogue?: ReactNode
  // When true, this is a real readable Plan A epilogue (the main path or the
  // inline continuation after another plan), so cap it with the shared
  // end-of-scenario footer (heading + timeline diagram + reviewer box) after the
  // epilogue prose. Left false for preview/teaser renders so they stay clean.
  // Owning the cap here — rather than as a per-caller slot — keeps every full
  // epilogue ending identical and stops new call sites from silently dropping it.
  showScenarioEndCap?: boolean
  proseClassName?: string
}) => {
  const markdownClassName = proseClassName
    ? `markdown-content ${proseClassName}`
    : 'markdown-content'
  const chunks = useMemo(() => {
    if (!node.content?.markdown) return []
    return getPlaybookBranchNodeChunks(
      nodeId,
      node.content.markdown,
      node.title,
      node.footnotesMarkdown
    )
  }, [nodeId, node.content?.markdown, node.title, node.footnotesMarkdown])

  const { regularSections, epilogueSections } = useMemo(() => {
    const sections = node.sections as ResolvedBranchNodeSection[] | undefined
    if (!sections) {
      return {
        regularSections: [] as RenderedSection[],
        epilogueSections: [] as RenderedSection[],
      }
    }
    const nextRegularSections: RenderedSection[] = []
    const nextEpilogueSections: RenderedSection[] = []
    let hasReachedEpilogue = false
    for (const section of sections) {
      if (section.epilogueTitle) {
        hasReachedEpilogue = true
      }
      const nextSection = {
        key: section.id,
        section,
        chunks: section.content?.markdown
          ? getPlaybookBranchNodeChunks(
            section.id,
            section.content.markdown,
            section.title,
            node.footnotesMarkdown
          )
          : [],
      }
      if (hasReachedEpilogue) {
        nextEpilogueSections.push(nextSection)
      } else {
        nextRegularSections.push(nextSection)
      }
    }
    return { regularSections: nextRegularSections, epilogueSections: nextEpilogueSections }
  }, [node.sections, node.footnotesMarkdown])

  // Which ending the props ask for, run through the sequencer so switching
  // endings animates the open one closed first, then opens the target.
  const endingTarget: EndingKey | null = isEpilogueExpanded ? 'epilogue' : activePov
  const displayedEnding = useSequencedEnding(endingTarget)

  // Pre-mount all three ending bodies at height 0 — but only after the first
  // commit has painted, so the visible content (including the end-cap nav grid
  // in `beforeEpilogue`) renders before we pay for the hidden bodies. After
  // that, clicking an ending button is a pure height animation, no mount work.
  const [prerenderEndings, setPrerenderEndings] = useState(false)
  const hasEndings = epilogueSections.length > 0
  useEffect(() => {
    if (hasEndings) setPrerenderEndings(true)
  }, [hasEndings])

  const content = (
    <>
      <div
        className={markdownClassName}
        data-playbook-timeline-segment={markdownTimelineSegmentId(nodeId)}
      >
        {chunks.map((chunk, i) => (
          <Markdown
            key={i}
            nodes={chunk.nodes}
            docContext={chunk.docContext}
            onPlanHover={onPlanHover}
            onBranchHover={onBranchHover}
          />
        ))}
      </div>
      {regularSections.map(({ key, section, chunks: sChunks }) => (
        <div
          key={key}
          className={markdownClassName}
          id={section?.id}
          data-playbook-timeline-segment={
            section ? sectionTimelineSegmentId(section.id) : undefined
          }
        >
          {sChunks.length > 0
            ? sChunks.map((chunk, i) => (
              <Markdown
                key={i}
                nodes={chunk.nodes}
                docContext={chunk.docContext}
                onPlanHover={onPlanHover}
                onBranchHover={onBranchHover}
                />
            ))
            : section?.title && (
              <h1 className="text-2xl md:text-4xl mt-[1.5em]">
                {section.title}
              </h1>
            )}
        </div>
      ))}
      {epilogueSections.length > 0 && (
        <>
        {beforeEpilogue}
        {/* The end-of-Plan-A diagram (beforeEpilogue) is the single canonical hub:
            the Epilogue and the two POV endings all expand right here below it,
            one at a time (Playbook's ending state keeps them mutually exclusive).
            The wrapper owns the ENTIRE gap between the end-cap nav grid and an
            opened ending: exactly 75px (mt collapses with the grid's mb-8; the
            bodies' own leading margins are zeroed / contained by EndingCollapse's
            flow-root). The bottom padding reserves visible room under the
            buttons so the body animates in on screen instead of below the fold.
            Skipped for preview renders that carry no end cap. */}
        <div className={beforeEpilogue ? "mt-[75px] pb-[300px]" : undefined}>
        <PlaybookEpilogue
          title={epilogueSections[0].section?.epilogueTitle || 'Epilogue'}
          isExpanded={displayedEnding === 'epilogue'}
          prerender={prerenderEndings}
          afterContent={showScenarioEndCap ? <ScenarioEndFooter ending="epilogue" /> : undefined}
        >
          {epilogueSections.map(({ key, section, chunks: sChunks }) => (
            <div
              key={key}
              className={markdownClassName}
              id={section?.id}
              data-playbook-timeline-segment={
                section ? sectionTimelineSegmentId(section.id) : undefined
              }
            >
              {sChunks.length > 0
                ? sChunks.map((chunk, i) => (
                  <Markdown
                    key={i}
                    nodes={chunk.nodes}
                    docContext={chunk.docContext}
                    onPlanHover={onPlanHover}
                    onBranchHover={onBranchHover}
                        />
                ))
                : !section?.epilogueTitle && section?.title && (
                  <h1 className="text-2xl md:text-4xl mt-[1.5em]">
                    {section.title}
                  </h1>
                )}
            </div>
          ))}
        </PlaybookEpilogue>
        {(['public', 'insider'] as const).map((pov) =>
          perspectiveBodies?.[pov] && (prerenderEndings || displayedEnding === pov) ? (
            <EndingCollapse key={pov} open={displayedEnding === pov}>
              <PerspectivePovBody
                perspective={pov}
                markdown={perspectiveBodies[pov]!}
              />
            </EndingCollapse>
          ) : null,
        )}
        </div>
        </>
      )}
    </>
  )

  if (fadeOut) {
    return (
      <div style={{ maskImage: FADE_OUT_MASK_IMAGE, WebkitMaskImage: FADE_OUT_MASK_IMAGE }}>
        {content}
      </div>
    )
  }

  return content
}
